import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Download,
  FileText,
  Loader2,
  AlertCircle,
  Eye,
  FolderOpen,
  Shield,
  Image as ImageIcon,
  File,
  ChevronRight,
  X,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
} from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import easyfinderLogo from "@/assets/easyfinder-logo-google.png";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// PDF.js worker configured in src/lib/pdfConfig.ts

interface CollectionDocument {
  id: string;
  title: string;
  file_name: string;
  file_path: string;
  file_size: number | null;
  mime_type: string | null;
  doc_type: string;
  sort_order: number;
  signedUrl: string | null;
}

interface CollectionData {
  id: string;
  name: string;
  description: string | null;
  permission: string;
  expires_at: string | null;
  view_count: number;
}

const TYPE_LABELS: Record<string, string> = {
  invoice: "Facture",
  contract: "Contrat",
  tax: "Document fiscal",
  insurance: "Assurance",
  receipt: "Reçu",
  quote: "Devis",
  identity: "Identité",
  payslip: "Bulletin de salaire",
  attestation: "Attestation",
  bank_statement: "Relevé bancaire",
  medical_certificate: "Certificat médical",
  notarial_deed: "Acte notarié",
  administrative: "Administratif",
  warranty: "Garantie",
  other: "Document",
};

const TYPE_EMOJIS: Record<string, string> = {
  invoice: "🧾",
  contract: "📝",
  tax: "📊",
  insurance: "🛡️",
  receipt: "🧾",
  quote: "💰",
  identity: "🪪",
  payslip: "💵",
  attestation: "📋",
  bank_statement: "🏦",
  medical_certificate: "🏥",
  notarial_deed: "⚖️",
  administrative: "📁",
  warranty: "🔧",
  other: "📄",
};

const formatFileSize = (bytes: number | null) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SharedCollection = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collection, setCollection] = useState<CollectionData | null>(null);
  const [documents, setDocuments] = useState<CollectionDocument[]>([]);
  const [previewDoc, setPreviewDoc] = useState<CollectionDocument | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!token) {
      setError("Lien invalide");
      setLoading(false);
      return;
    }

    const fetchCollection = async () => {
      try {
        const { data, error: fetchError } = await supabase.functions.invoke(
          "get-shared-collection",
          { body: { token } }
        );

        if (fetchError) throw fetchError;

        if (!data || data.error) {
          setError(data?.error || "Cette collection n'existe pas ou a expiré.");
          setLoading(false);
          return;
        }

        setCollection(data.collection);
        setDocuments(data.documents);
      } catch (err: any) {
        console.error("Error:", err);
        setError("Une erreur est survenue.");
      } finally {
        setLoading(false);
      }
    };

    fetchCollection();
  }, [token]);

  const handleDownload = async (doc: CollectionDocument) => {
    if (!doc.signedUrl) return;
    const a = window.document.createElement("a");
    a.href = doc.signedUrl;
    a.download = doc.file_name;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
  };

  const handleDownloadAll = async () => {
    for (const doc of documents) {
      if (doc.signedUrl) {
        await handleDownload(doc);
        // Small delay between downloads
        await new Promise((r) => setTimeout(r, 500));
      }
    }
  };

  const EasyFinderLogo = ({ size = "default" }: { size?: "small" | "default" | "large" }) => {
    const sizes = {
      small: { img: "w-8 h-8", text: "text-base" },
      default: { img: "w-10 h-10", text: "text-xl" },
      large: { img: "w-14 h-14", text: "text-2xl" },
    };
    const s = sizes[size];

    return (
      <div className="flex items-center gap-2.5">
        <img
          src={easyfinderLogo}
          alt="EasyFinder"
          className={cn(s.img, "rounded-xl shadow-lg shadow-primary/25")}
        />
        <span
          className={cn(
            s.text,
            "font-bold bg-gradient-to-r from-primary to-indigo-600 bg-clip-text text-transparent"
          )}
        >
          EasyFinder
        </span>
      </div>
    );
  };

  const isPdf = (mimeType: string | null) => mimeType === "application/pdf";
  const isImage = (mimeType: string | null) => mimeType?.startsWith("image/");

  const getFileIcon = (doc: CollectionDocument) => {
    if (isPdf(doc.mime_type)) return <FileText className="w-5 h-5" />;
    if (isImage(doc.mime_type)) return <ImageIcon className="w-5 h-5" />;
    return <File className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-6">
          <EasyFinderLogo size="large" />
          <div className="flex flex-col items-center gap-2">
            <div className="relative">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <FolderOpen className="w-8 h-8 text-primary" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2">Chargement de la collection...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30 flex flex-col">
        <header className="border-b border-border/40 bg-white/80 backdrop-blur-xl">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
            <button onClick={() => navigate("/")} className="hover:opacity-80 transition-opacity">
              <EasyFinderLogo />
            </button>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center">
            <div className="w-20 h-20 rounded-3xl bg-destructive/10 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold mb-3">Collection inaccessible</h1>
            <p className="text-muted-foreground mb-8">{error}</p>
            <Button onClick={() => navigate("/")} size="lg" className="rounded-xl">
              Découvrir EasyFinder
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 flex flex-col">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-50 backdrop-blur-2xl bg-white/90 border-b border-border/40 shadow-sm"
      >
        <div className="px-4 sm:px-6 py-3">
          <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
            <motion.button
              onClick={() => navigate("/")}
              className="hover:opacity-80 transition-opacity shrink-0"
              whileHover={{ scale: 1.02 }}
            >
              <EasyFinderLogo size="small" />
            </motion.button>

            <div className="flex items-center gap-3 min-w-0 flex-1 justify-center">
              <div className="hidden sm:flex w-11 h-11 rounded-xl bg-gradient-to-br from-primary/10 to-indigo-100 items-center justify-center">
                <FolderOpen className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0 text-center sm:text-left">
                <h1 className="font-semibold text-sm sm:text-base leading-tight line-clamp-1">
                  {collection?.name}
                </h1>
                <div className="flex items-center justify-center sm:justify-start gap-2 mt-0.5">
                  <Badge variant="secondary" className="text-[10px] font-medium bg-primary/10 text-primary border-0">
                    {documents.length} document{documents.length > 1 ? "s" : ""}
                  </Badge>
                  <span className="text-[10px] sm:text-xs text-muted-foreground flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    {collection?.view_count}
                  </span>
                </div>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadAll}
              className="h-9 gap-2 rounded-lg border-primary/20 hover:bg-primary/5"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Tout télécharger</span>
            </Button>
          </div>
        </div>
      </motion.header>

      {/* Security Badge */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="bg-gradient-to-r from-primary/5 via-indigo-50/50 to-primary/5 border-b border-primary/10"
      >
        <div className="max-w-5xl mx-auto px-4 py-2 flex items-center justify-center gap-2">
          <Shield className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs text-primary/80 font-medium">
            Collection partagée en toute sécurité via EasyFinder
          </span>
        </div>
      </motion.div>

      {/* Description */}
      {collection?.description && (
        <div className="max-w-5xl mx-auto px-4 py-4 w-full">
          <p className="text-sm text-muted-foreground">{collection.description}</p>
        </div>
      )}

      {/* Document List */}
      <div className="flex-1 max-w-5xl mx-auto px-4 py-6 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid gap-3"
        >
          {documents.map((doc, index) => (
            <motion.div
              key={doc.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * index }}
            >
              <Card
                className={cn(
                  "p-4 hover:shadow-md transition-all cursor-pointer border-border/50",
                  "hover:border-primary/30 hover:bg-primary/5"
                )}
                onClick={() => {
                  setPreviewDoc(doc);
                  setCurrentPage(1);
                  setScale(1);
                }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/10 to-indigo-100 flex items-center justify-center shrink-0">
                    <span className="text-xl">{TYPE_EMOJIS[doc.doc_type] || "📄"}</span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm line-clamp-1">{doc.title}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary" className="text-[10px] bg-muted/50">
                        {TYPE_LABELS[doc.doc_type] || "Document"}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">
                        {formatFileSize(doc.file_size)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-lg"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(doc);
                      }}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* CTA Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="border-t border-border/40 bg-gradient-to-r from-primary/5 via-white to-indigo-50/50"
      >
        <div className="max-w-5xl mx-auto px-4 py-6 text-center">
          <h3 className="font-semibold text-lg mb-2">Gérez vos documents intelligemment</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Recherche IA, classement automatique, partage sécurisé
          </p>
          <Button onClick={() => navigate("/auth")} className="rounded-xl">
            Essayer EasyFinder gratuitement
          </Button>
        </div>
      </motion.div>

      {/* Preview Modal */}
      {previewDoc && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex flex-col"
          onClick={() => setPreviewDoc(null)}
        >
          {/* Modal Header */}
          <div
            className="flex items-center justify-between p-4 bg-white/10 backdrop-blur-xl border-b border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                {getFileIcon(previewDoc)}
              </div>
              <div>
                <h3 className="font-medium text-white text-sm line-clamp-1">{previewDoc.title}</h3>
                <p className="text-xs text-white/60">{previewDoc.file_name}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isPdf(previewDoc.mime_type) && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-xs text-white/80 min-w-[50px] text-center">
                    {currentPage}/{numPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10"
                    onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
                    disabled={currentPage >= numPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <div className="w-px h-6 bg-white/20 mx-2" />
                </>
              )}

              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10"
                onClick={() => setScale((s) => Math.max(0.5, s - 0.25))}
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="text-xs text-white/80 min-w-[40px] text-center">
                {Math.round(scale * 100)}%
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10"
                onClick={() => setScale((s) => Math.min(2, s + 0.25))}
              >
                <ZoomIn className="w-4 h-4" />
              </Button>

              <div className="w-px h-6 bg-white/20 mx-2" />

              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-white/80 hover:text-white hover:bg-white/10 gap-2"
                onClick={() => handleDownload(previewDoc)}
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Télécharger</span>
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10"
                onClick={() => setPreviewDoc(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Preview Content */}
          <div
            className="flex-1 overflow-auto flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            {isPdf(previewDoc.mime_type) && previewDoc.signedUrl ? (
              <Document
                file={previewDoc.signedUrl}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                loading={
                  <div className="flex items-center gap-3 py-20">
                    <Loader2 className="w-5 h-5 animate-spin text-white" />
                    <span className="text-white/60">Chargement...</span>
                  </div>
                }
              >
                <Page
                  pageNumber={currentPage}
                  scale={scale}
                  className="shadow-2xl rounded-lg overflow-hidden"
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  width={Math.min(window.innerWidth - 64, 900)}
                />
              </Document>
            ) : isImage(previewDoc.mime_type) && previewDoc.signedUrl ? (
              <img
                src={previewDoc.signedUrl}
                alt={previewDoc.title}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                style={{ transform: `scale(${scale})` }}
              />
            ) : (
              <div className="text-center text-white/60">
                <File className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Aperçu non disponible</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => handleDownload(previewDoc)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger le fichier
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default SharedCollection;
